<template>
	<BaseLayout :footer-full="true" :include-padding="false" :background-color-class="`bg-color-white`">
		<section class="blog-section article-list--featured container pt-150 pb-50 py-300-md">
			<div class="gutters-300-md display-flex-md">
				<BasePlaceHolder :line-count="8" :placehold="loading" class="col-7-md col-8-lg">
					<ArticleCardLarge
						v-for="(feature, fId) in featuredArticles.slice(0, 1)"
						:key="fId"
						:article="feature"
						:classes="'featured-article'"
					/>
				</BasePlaceHolder>
				<div class="featured-aside col-5-md col-4-lg bl-md">
					<BasePlaceHolder
						:line-count="8"
						class="mt-150 mt-0-md pt-50 pt-0-md pb-150-md bt-smo"
						:placehold="loading"
					>
						<h2 class="heading-5 mb-200 display-none-smo">Featured</h2>

						<article
							v-for="(article, faId) in featuredArticles.slice(1, 5)"
							:key="faId"
							class="article-list-item divider"
						>
							<BaseLink
								:link="{
									router: 'blog-post',
									params: { blogPostUrlSlug: article.slug.current }
								}"
							>
								<p v-if="article.category?.name" class="overline eta">
									{{ article.category.name }}
								</p>
								<p class="heading-6 mt-50 article-title">
									{{ article.title }}
								</p>
							</BaseLink>
						</article>
					</BasePlaceHolder>
				</div>
			</div>

			<BasePlaceHolder :line-count="10" :placehold="loading" :class="{ 'mt-300 bt pt-300 pb-300': loading }" />
		</section>

		<div v-if="!loading && page?.pageComponents" class="page-components-container blog-section">
			<section v-for="(component, componentId) in pageComponents" :key="componentId" class="blog-section">
				<component :is="component.component" v-bind="component.props"></component>
			</section>
		</div>
	</BaseLayout>
</template>

<script setup lang="ts">
import { getBlogArticlesByCategory, getSanityBlogLanding } from '@api/sanity/graphql/blog-education-center';
import { markRaw, onUnmounted, ref } from 'vue';
import {
	SanityArticlesSeries,
	SanityArticleTeaser,
	SanityBlogCategoryWithArticles,
	SanityBlogFeaturedCategories,
	SanityBlogLanding,
	SanityBlogLandingCta
} from 'types/sanity/education-center';
import ArticleCardLarge from '@components/blog/article-card-large.vue';
import BlogSeries from '@components/blog/blog-series.vue';
import { CallToAction } from 'types/layout';
import CTABanner from '@components/layout/cta-banner.vue';
import { DynamicComponent } from 'types/vue';
import FeaturedCategories from '@components/blog/featured-categories.vue';
import { mapInvestorUpdateArticleTeasers } from '@utils/blog';
import { useHead } from '@unhead/vue';
import { useRouter } from 'vue-router';

const router = useRouter();

const featuredArticles = ref<Array<SanityArticleTeaser>>([]);
const page = ref<SanityBlogLanding | null>(null);
const pageComponents = ref<Array<DynamicComponent>>([]);

const headControl = useHead({
	script: [`window.prerenderReady = false;`]
});
const loading = ref(true);

async function setup(): Promise<void> {
	const sanityLandingResponse = await getSanityBlogLanding();

	if (sanityLandingResponse) {
		page.value = sanityLandingResponse;
		const metaItems = {
			title: page.value.title,
			meta: [
				{ property: 'og:title', content: page.value.title },
				{ name: 'description', content: page.value.summary },
				{ property: 'og:description', content: page.value.summary }
			]
		};

		headControl?.patch({
			...metaItems,
			script: [`window.prerenderReady = false;`]
		});

		featuredArticles.value = mapInvestorUpdateArticleTeasers(
			(sanityLandingResponse?.featuredPosts ?? []).filter((post) => post?.slug)
		);

		// Dynamic Page Components
		for (const component of sanityLandingResponse?.pageComponents ?? []) {
			switch (component._type) {
				case 'articlesSeries': {
					const seriesComponentType = component as SanityArticlesSeries;
					pageComponents.value.push({
						component: markRaw(BlogSeries),
						props: {
							series: seriesComponentType
						}
					});
					break;
				}
				case 'blogFeaturedCategories': {
					const featuredCategoriesAndBlogs = ref<Array<SanityBlogCategoryWithArticles>>([]);

					const featuredComponentType = component as SanityBlogFeaturedCategories;
					const landingPageCategories = featuredComponentType.featuredCategories;

					if (landingPageCategories.length > 0) {
						for (const cat of landingPageCategories) {
							const articles = await getBlogArticlesByCategory(cat.slug.current, 2, 'blog-landing-view');

							featuredCategoriesAndBlogs.value.push({
								category: cat,
								articles: articles
							} as SanityBlogCategoryWithArticles);
						}
					}

					pageComponents.value.push({
						component: markRaw(FeaturedCategories),
						props: {
							featuredCategories: featuredCategoriesAndBlogs.value
						}
					});
					break;
				}
				case 'blogLandingCta': {
					const ctaComponent = ref<CallToAction | undefined>(undefined);
					const ctaComponentType = component as SanityBlogLandingCta;

					ctaComponent.value = {
						heading: ctaComponentType.ctaHeading,
						summary: ctaComponentType?.ctaCopy ?? '',
						link: {
							text: ctaComponentType.ctaLinkText,
							href: ctaComponentType.ctaLinkUrl
						}
					};
					pageComponents.value.push({
						component: markRaw(CTABanner),
						props: {
							cta: ctaComponent.value
						}
					});
					break;
				}
				default: {
					break;
				}
			}
		}

		headControl?.patch({
			...metaItems,
			script: [`window.prerenderReady = true;`]
		});

		loading.value = false;
	} else {
		await router.push({
			path: '/404'
		});
	}
}

setup();

onUnmounted(() => {
	headControl?.dispose();
});
</script>

<style lang="scss">
@use '../../styles/constants/colors' as *;
@use '../../styles/utilities/respond-to.scss' as *;

.featured-aside {
	@include respond-to(md) {
		background: none;
	}
}
.blog-section + .blog-section {
	position: relative;

	&::before {
		background: gray(20);
		content: '';
		height: 1px;
		left: 50%;
		max-width: $max-container-width;
		position: absolute;
		top: 0;
		transform: translateX(-50%);
		width: calc(100% - 2rem);

		@include respond-to(md) {
			width: calc(100% - 3rem);
		}

		@include respond-to(lg) {
			width: calc(1200px - 6rem);
		}
	}
}
</style>
